import { IModalContext, ModalChildComponentContextKey } from '@AppModels/.';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '@AppModels/AppState';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useMergeCompaniesMutation } from '../../../../queries/Company';
import { baseStyleSheet } from '../../../styles/styles';
import { LoadingSpinner } from '../../LoadingSpinner';
import { RadioButton } from '../../RadioButton';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps, IModalContext<Api.ICompany>, IErrorMessageComponentProps {
	selectedCompanies?: Api.ICompany[];
	onClose: () => void;
}

const _MergeCompaniesWizard: React.FC<IProps> = props => {
	const { errorMessages, selectedCompanies, onClose } = props;
	const [selectedCompany, setSelectedCompany] = useState<Api.ICompany | undefined>(undefined);
	const [isMerging, setIsMerging] = useState<boolean>(false);
	const userSession = useUserSession();
	const mergeCompaniesMutation = useMergeCompaniesMutation({
		impersonationContext: userSession.impersonationContext,
	});

	const onRenderCompanyNameWithDomain = (company: Api.ICompany) => {
		const emailDomain =
			!!company.emailDomains && company.emailDomains.length > 0 ? ` (${company.emailDomains[0]})` : '';
		return `${company.companyName}${emailDomain}`;
	};

	const onCompanySelected = (company: Api.ICompany) => () => {
		setSelectedCompany(company);
	};

	const mergeClick = () => {
		if (!selectedCompany) {
			return;
		}

		const companiesToMerge = (selectedCompanies || []).filter(x => x.id !== selectedCompany.id).map(x => x.id);

		if (companiesToMerge.length === 0) {
			return;
		}

		setIsMerging(true);

		try {
			mergeCompaniesMutation.mutate({
				targetId: selectedCompany.id,
				companyIds: companiesToMerge,
			});
			onClose();
		} catch (error) {
			setIsMerging(false);
			errorMessages.pushApiError(error as Api.IOperationResultNoValue);
		}
	};

	return (
		<div className={css(styleSheet.container)}>
			{!!selectedCompany && isMerging ? (
				<LoadingSpinner className='absolute-center' />
			) : (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.header)}>Merge companies</div>
					<div className={css(styleSheet.subheader)}>
						Please select the <strong>main</strong> email domain for this company
					</div>
					<div className={css(styleSheet.list)}>
						{!!selectedCompanies &&
							selectedCompanies.map(x => {
								return (
									<div className={css(styleSheet.company)} key={x.id} onClick={onCompanySelected(x)}>
										<RadioButton
											checked={x === selectedCompany}
											className={css(styleSheet.radioButton)}
											id={`merge-company-radio-${x.id}`}
											name='merge-company-radio-group'
											readOnly={true}
										/>
										<span className={css(styleSheet.companyNameWithEmailDomain, baseStyleSheet.truncateText)}>
											{onRenderCompanyNameWithDomain(x)}
										</span>
									</div>
								);
							})}
					</div>
					<footer className={css(styleSheet.footer)}>
						<button className={css(baseStyleSheet.ctaButton)} onClick={mergeClick}>
							Merge
						</button>

						<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onClose}>
							Cancel
						</button>
					</footer>
				</div>
			)}
		</div>
	);
};

const MergeCompaniesWizardAsObserver = observer(_MergeCompaniesWizard);
export const MergeCompaniesWizard = inject(
	UserSessionViewModelKey,
	ModalChildComponentContextKey,
	ErrorMessagesViewModelKey
)(MergeCompaniesWizardAsObserver);
